import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { 
  primary: '#2b3380', 
  whatsapp: '#25D366', 
  success: '#4caf50', 
  warning: '#ff9800', 
  error: '#f44336',
  info: '#2196F3'
};

function WhatsAppContacts({ user, onLogout }) {
  // State Management
  const [activeTab, setActiveTab] = useState('directory'); // directory, segments, import-export, timeline
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filters, setFilters] = useState({ source: '', tag: '', segment: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  
  // Modals
  const [showAddContact, setShowAddContact] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  
  // Form States
  const [csvFile, setCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [newContact, setNewContact] = useState({
    name: '',
    phone_number: '',
    email: '',
    source: 'manual',
    tags: [],
    segments: [],
    notes: ''
  });
  const [bulkAction, setBulkAction] = useState({ type: '', value: '' });
  const [newSegment, setNewSegment] = useState({ name: '', description: '' });
  const [timeline, setTimeline] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [filters, activeTab]);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const params = new URLSearchParams();
      if (filters.source) params.append('source', filters.source);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.segment) params.append('segment', filters.segment);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '100');

      const [contactsRes, statsRes, segmentsRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/contacts?${params}`, config),
        axios.get(`${API}/api/whatsapp/contacts/stats`, config),
        axios.get(`${API}/api/whatsapp/contacts/segments`, config)
      ]);

      setContacts(contactsRes.data.contacts || []);
      setPagination({
        page: contactsRes.data.page || 1,
        total: contactsRes.data.total || 0,
        pages: contactsRes.data.pages || 1
      });
      setStats(statsRes.data.stats);
      setSegments(segmentsRes.data.segments || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/contacts/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Contact sync started! Syncing customers and leads...');
      setTimeout(fetchAllData, 2000);
    } catch (error) {
      alert('Error syncing: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/contacts`, newContact, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Contact added successfully!');
      setShowAddContact(false);
      setNewContact({ name: '', phone_number: '', email: '', source: 'manual', tags: [], segments: [], notes: '' });
      fetchAllData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    try {
      const text = await csvFile.text();
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/whatsapp/contacts/import-csv`,
        { csv_data: text, auto_detect_duplicates: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImportResults(response.data);
      alert(`✅ Import completed! Imported: ${response.data.imported}, Duplicates: ${response.data.duplicates}, Errors: ${response.data.errors}`);
      setCsvFile(null);
      fetchAllData();
    } catch (error) {
      alert('Error importing: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkAction = async () => {
    if (selectedContacts.length === 0) {
      alert('Please select contacts first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (bulkAction.type === 'tag' && bulkAction.value) {
        await axios.post(`${API}/api/whatsapp/contacts/bulk-tag`, 
          { contact_ids: selectedContacts, tag: bulkAction.value },
          config
        );
      } else if (bulkAction.type === 'segment' && bulkAction.value) {
        await axios.post(`${API}/api/whatsapp/contacts/bulk-segment`,
          selectedContacts,
          { params: { segment: bulkAction.value } },
          config
        );
      } else if (bulkAction.type === 'delete') {
        if (!window.confirm(`Delete ${selectedContacts.length} contacts?`)) return;
        await axios.post(`${API}/api/whatsapp/contacts/bulk-delete`,
          selectedContacts,
          config
        );
      }

      alert('✅ Bulk action completed!');
      setShowBulkActions(false);
      setSelectedContacts([]);
      setBulkAction({ type: '', value: '' });
      fetchAllData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateSegment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/whatsapp/contacts/segments`,
        { name: newSegment.name, description: newSegment.description, filters: {} },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Segment created!');
      setNewSegment({ name: '', description: '' });
      setShowSegmentModal(false);
      fetchAllData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Delete this segment?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/whatsapp/contacts/segments/${segmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Segment deleted!');
      fetchAllData();
    } catch (error) {
      alert('Error deleting segment');
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.source) params.append('source', filters.source);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.segment) params.append('segment', filters.segment);

      const response = await axios.get(`${API}/api/whatsapp/contacts/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (format === 'csv') {
        const blob = new Blob([response.data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
      }

      alert(`✅ Exported ${response.data.count} contacts`);
    } catch (error) {
      alert('Error exporting contacts');
    }
  };

  const handleViewTimeline = async (contact) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/contacts/${contact.id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(response.data);
      setSelectedContact(contact);
      setShowTimelineModal(true);
    } catch (error) {
      alert('Error loading timeline');
    }
  };

  const toggleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">📞</div>
            <p className="text-xl text-gray-600">Loading contacts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            📞 WhatsApp Contact Directory
          </h1>
          <p className="text-gray-600">Manage contacts for WhatsApp campaigns and automation</p>
        </div>

        {/* Stats Dashboard */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: COLORS.primary }}>
              <div className="text-sm text-gray-600 mb-1">Total Contacts</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.primary }}>{stats.total_contacts || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: COLORS.success }}>
              <div className="text-sm text-gray-600 mb-1">Recent (30 days)</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.success }}>{stats.recent_contacts || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: COLORS.warning }}>
              <div className="text-sm text-gray-600 mb-1">Customers</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.warning }}>{stats.by_source?.customer || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: COLORS.info }}>
              <div className="text-sm text-gray-600 mb-1">Leads</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.info }}>{stats.by_source?.lead || 0}</div>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('directory')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'directory'
                  ? 'border-b-2 text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'directory' ? { borderColor: COLORS.primary, color: COLORS.primary } : {}}
            >
              📋 Contact Directory
            </button>
            <button
              onClick={() => setActiveTab('segments')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'segments'
                  ? 'border-b-2 text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'segments' ? { borderColor: COLORS.primary, color: COLORS.primary } : {}}
            >
              🎯 Segments ({segments.length})
            </button>
            <button
              onClick={() => setActiveTab('import-export')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'import-export'
                  ? 'border-b-2 text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'import-export' ? { borderColor: COLORS.primary, color: COLORS.primary } : {}}
            >
              🔄 Import / Export
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'directory' && (
          <>
            {/* Action Bar */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowAddContact(true)}
                  className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: COLORS.whatsapp }}
                >
                  ➕ Add Contact
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: COLORS.info }}
                >
                  {syncing ? '⏳ Syncing...' : '🔄 Sync from CRM'}
                </button>
                {selectedContacts.length > 0 && (
                  <button
                    onClick={() => setShowBulkActions(true)}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: COLORS.warning }}
                  >
                    ⚡ Bulk Actions ({selectedContacts.length})
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="font-semibold mb-3">🔍 Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Search name, phone, email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ focusRingColor: COLORS.primary }}
                />
                <select
                  value={filters.source}
                  onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                >
                  <option value="">All Sources</option>
                  <option value="customer">Customers</option>
                  <option value="lead">Leads</option>
                  <option value="vendor">Vendors</option>
                  <option value="manual">Manual</option>
                </select>
                <select
                  value={filters.segment}
                  onChange={(e) => setFilters({ ...filters, segment: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                >
                  <option value="">All Segments</option>
                  {segments.map(seg => (
                    <option key={seg.id} value={seg.name}>{seg.name} ({seg.contact_count})</option>
                  ))}
                </select>
                <button
                  onClick={() => setFilters({ source: '', tag: '', segment: '', search: '' })}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Contacts Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">Contacts ({pagination.total})</h3>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedContacts.length === contacts.length && contacts.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">Select All</span>
                </label>
              </div>
              
              {contacts.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-xl text-gray-600 mb-2">No contacts found</p>
                  <p className="text-sm text-gray-500">Add contacts manually or sync from CRM</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Select</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segments</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedContacts.includes(contact.id)}
                              onChange={() => toggleSelectContact(contact.id)}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{contact.name}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{contact.phone_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{contact.email || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              contact.source === 'customer' ? 'bg-green-100 text-green-700' :
                              contact.source === 'lead' ? 'bg-blue-100 text-blue-700' :
                              contact.source === 'vendor' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {contact.source}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(contact.tags || []).slice(0, 2).map((tag, idx) => (
                                <span key={idx} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                                  {tag}
                                </span>
                              ))}
                              {(contact.tags || []).length > 2 && (
                                <span className="text-xs text-gray-500">+{contact.tags.length - 2}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(contact.segments || []).slice(0, 2).map((seg, idx) => (
                                <span key={idx} className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded">
                                  {seg}
                                </span>
                              ))}
                              {(contact.segments || []).length > 2 && (
                                <span className="text-xs text-gray-500">+{contact.segments.length - 2}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleViewTimeline(contact)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View Timeline
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'segments' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>🎯 Contact Segments</h2>
              <button
                onClick={() => setShowSegmentModal(true)}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: COLORS.success }}
              >
                ➕ Create Segment
              </button>
            </div>

            {segments.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-xl text-gray-600 mb-2">No segments created yet</p>
                <p className="text-sm text-gray-500">Create segments to organize your contacts</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {segments.map((seg) => (
                  <div key={seg.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg" style={{ color: COLORS.primary }}>{seg.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{seg.description || 'No description'}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteSegment(seg.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                      <span className="text-2xl font-bold" style={{ color: COLORS.info }}>{seg.contact_count || 0}</span>
                      <span className="text-sm text-gray-500">contacts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'import-export' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>🔄 Import / Export Contacts</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Import Section */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span>📤</span> Import Contacts
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a CSV file to import contacts. File should have columns: name, phone_number, email, tags, segments
                </p>
                <div className="mb-4">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                {csvFile && (
                  <p className="text-sm text-gray-600 mb-4">Selected: {csvFile.name}</p>
                )}
                <button
                  onClick={handleImportCSV}
                  disabled={!csvFile}
                  className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ backgroundColor: COLORS.success }}
                >
                  📤 Import CSV
                </button>
                {importResults && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Import Results:</h4>
                    <p className="text-sm">✅ Imported: {importResults.imported}</p>
                    <p className="text-sm">⚠️ Duplicates: {importResults.duplicates}</p>
                    <p className="text-sm">❌ Errors: {importResults.errors}</p>
                  </div>
                )}
              </div>

              {/* Export Section */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span>📥</span> Export Contacts
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Export your contacts to CSV or JSON format. Current filters will be applied.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: COLORS.success }}
                  >
                    📥 Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: COLORS.info }}
                  >
                    📥 Export as JSON
                  </button>
                </div>
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>Tip:</strong> Use filters before exporting to export specific segments or sources.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Contact Modal */}
        {showAddContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>➕ Add Contact</h3>
                <button onClick={() => setShowAddContact(false)} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
              </div>
              <form onSubmit={handleAddContact} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    value={newContact.phone_number}
                    onChange={(e) => setNewContact({ ...newContact, phone_number: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    value={newContact.source}
                    onChange={(e) => setNewContact({ ...newContact, source: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  >
                    <option value="manual">Manual</option>
                    <option value="customer">Customer</option>
                    <option value="lead">Lead</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newContact.notes}
                    onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: COLORS.success }}
                >
                  ✨ Add Contact
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Actions Modal */}
        {showBulkActions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>⚡ Bulk Actions</h3>
                <button onClick={() => setShowBulkActions(false)} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Selected: {selectedContacts.length} contacts</p>
              <div className="space-y-4">
                <select
                  value={bulkAction.type}
                  onChange={(e) => setBulkAction({ ...bulkAction, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                >
                  <option value="">Select Action</option>
                  <option value="tag">Add Tag</option>
                  <option value="segment">Add to Segment</option>
                  <option value="delete">Delete Contacts</option>
                </select>

                {(bulkAction.type === 'tag' || bulkAction.type === 'segment') && (
                  <input
                    type="text"
                    value={bulkAction.value}
                    onChange={(e) => setBulkAction({ ...bulkAction, value: e.target.value })}
                    placeholder={bulkAction.type === 'tag' ? 'Enter tag name' : 'Enter segment name'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                )}

                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction.type}
                  className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ backgroundColor: bulkAction.type === 'delete' ? COLORS.error : COLORS.success }}
                >
                  {bulkAction.type === 'delete' ? '🗑️ Delete' : '✨ Apply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Segment Modal */}
        {showSegmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>🎯 Create Segment</h3>
                <button onClick={() => setShowSegmentModal(false)} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
              </div>
              <form onSubmit={handleCreateSegment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Segment Name *</label>
                  <input
                    type="text"
                    value={newSegment.name}
                    onChange={(e) => setNewSegment({ ...newSegment, name: e.target.value })}
                    placeholder="e.g., VIP Customers, Mumbai Leads"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newSegment.description}
                    onChange={(e) => setNewSegment({ ...newSegment, description: e.target.value })}
                    placeholder="Describe this segment..."
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: COLORS.success }}
                >
                  ✨ Create Segment
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Timeline Modal */}
        {showTimelineModal && timeline && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>📊 Contact Timeline</h3>
                <button onClick={() => setShowTimelineModal(false)} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
              </div>
              
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-lg mb-2">{timeline.contact.name}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600">Phone:</span> {timeline.contact.phone_number}</div>
                  <div><span className="text-gray-600">Email:</span> {timeline.contact.email || 'N/A'}</div>
                  <div><span className="text-gray-600">Source:</span> {timeline.contact.source}</div>
                  <div><span className="text-gray-600">Total Messages:</span> {timeline.total_messages}</div>
                </div>
              </div>

              {/* Messages */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">💬 Recent Messages ({timeline.messages.length})</h4>
                {timeline.messages.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No messages yet</p>
                ) : (
                  <div className="space-y-2">
                    {timeline.messages.slice(0, 10).map((msg, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{msg.direction === 'outgoing' ? '➡️ Sent' : '⬅️ Received'}</span>
                          <span className="text-xs text-gray-500">{new Date(msg.logged_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-700">{msg.message_body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Campaigns */}
              <div>
                <h4 className="font-semibold mb-3">📢 Campaign History ({timeline.campaigns.length})</h4>
                {timeline.campaigns.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Not part of any campaigns yet</p>
                ) : (
                  <div className="space-y-2">
                    {timeline.campaigns.map((campaign, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium">{campaign.name}</h5>
                            <p className="text-sm text-gray-600 mt-1">{campaign.message}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            campaign.status === 'completed' ? 'bg-green-100 text-green-700' :
                            campaign.status === 'active' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {campaign.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default WhatsAppContacts;